<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n, type Locale } from '@/composables/useI18n'
import FlagIcon from '@/components/FlagIcon.vue'

const { locale, setLocale } = useI18n()
const open = ref(false)
const switcher = ref<HTMLElement | null>(null)

const languages: { code: Locale; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
]

function select(code: Locale) {
  setLocale(code)
  open.value = false
}

function onClickOutside(e: MouseEvent) {
  if (open.value && switcher.value && !switcher.value.contains(e.target as Node)) {
    open.value = false
  }
}

onMounted(() => document.addEventListener('click', onClickOutside))
onUnmounted(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
  <div ref="switcher" class="lang-switcher">
    <button class="lang-btn" :aria-expanded="open" aria-haspopup="true" :title="languages.find((l) => l.code === locale)?.label" @click="open = !open">
      <FlagIcon :code="locale" />
    </button>
    <div v-if="open" class="lang-dropdown">
      <button
        v-for="lang in languages"
        :key="lang.code"
        :class="['lang-option', { active: locale === lang.code }]"
        @click="select(lang.code)"
      >
        <FlagIcon :code="lang.code" />
        <span>{{ lang.label }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.lang-switcher {
  position: relative;
}

/* Matches the global .ui-icon-btn look */
.lang-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.2s var(--ease);
}

.lang-btn:hover,
.lang-btn[aria-expanded='true'] {
  border-color: var(--border-strong);
  background: var(--surface-3);
}

.lang-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.25rem;
  min-width: 150px;
  z-index: 200;
  box-shadow: var(--shadow-md);
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem 0.65rem;
  background: none;
  border: none;
  color: var(--text-muted);
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background 0.15s var(--ease), color 0.15s var(--ease);
}

.lang-option:hover {
  background: var(--surface-hover);
  color: var(--text);
}

.lang-option.active {
  background: var(--accent-soft);
  color: var(--accent-text);
}
</style>
